import { Grid2, Tab, Tabs, TextField, Typography } from "@mui/material";
import { useState } from "react";

import type { SectionSpec } from "@/shared/models/SectionSpec";
import { MILL_1 } from "@/setup/mill-config/mill-config";
import {
	SECTION_SPEC_FILE_KIND,
	type SectionSpecFileKind,
} from "@/shared/models/SectionSpec";
import { getSectionSpecCategoryTabs } from "../../models/SectionSpecCategoryTabs";
import type { SectionSpecDraftFormValues } from "../../models/SectionSpecForm";
import { formatEffectiveDate } from "../../models/SectionSpecFormat";
import type { SectionSpecFileChange } from "../../models/SectionSpecRotation";
import { SectionSpecDimensionsGrid } from "./SectionSpecDimensionsGrid";
import { SpecField } from "./SectionSpecFields";
import { SectionSpecImageField } from "./SectionSpecImageField";

export const SectionSpecReadOnlyView = ({
	spec,
	values,
	onSaveRotated,
}: {
	spec: SectionSpec;
	values: SectionSpecDraftFormValues;
	onSaveRotated?: (
		kind: SectionSpecFileKind,
		change: SectionSpecFileChange,
	) => Promise<void>;
}) => {
	const isM1 = spec.AssetId === MILL_1.id;
	const [selectedCategoryId, setSelectedCategoryId] = useState<number | null>(
		null,
	);
	const categoryTabs = isM1 ? getSectionSpecCategoryTabs(values.items) : [];
	const activeCategoryId = categoryTabs.some(
		(tab) => tab.id === selectedCategoryId,
	)
		? selectedCategoryId
		: categoryTabs[0]?.id;
	const visibleRows =
		isM1 && activeCategoryId != null
			? values.items.filter((row) => row.ProfileCategoryId === activeCategoryId)
			: values.items;

	return (
		<>
			<Grid2 container spacing={1}>
				<SpecField
					disabled
					field="printNo"
					label="Print No."
					value={values.printNo}
				/>
				<Grid2 size={{ xs: 12, sm: 4 }}>
					<TextField
						disabled
						label="Effective Date"
						size="small"
						fullWidth
						value={formatEffectiveDate(spec.EffectiveDate)}
					/>
				</Grid2>
				<SpecField
					disabled
					field="area"
					label="Area (sq.in.)"
					value={values.area}
				/>
				<SpecField
					disabled
					field="legDelta"
					label="Leg delta max"
					value={values.legDelta}
				/>
				<SpecField disabled field="min" label="Min lb/ft" value={values.min} />
				<SpecField
					disabled
					field="target"
					label="Target lb/ft"
					value={values.target}
				/>
				<SpecField disabled field="max" label="Max lb/ft" value={values.max} />
				<Grid2 size={{ xs: 12, sm: 4 }}>
					<SectionSpecImageField
						key={`${spec.ProfileSpecId}:${spec.MillDrawingUploadFileId}`}
						label="Mill Drawing"
						fileId={spec.MillDrawingUploadFileId ?? null}
						fileName={spec.MillDrawingFileName ?? null}
						onSaveRotated={
							onSaveRotated &&
							((change) =>
								onSaveRotated(SECTION_SPEC_FILE_KIND.MILL_DRAWING, change))
						}
					/>
				</Grid2>
				{!isM1 && (
					<Grid2 size={{ xs: 12, sm: 4 }}>
						<SectionSpecImageField
							key={`${spec.ProfileSpecId}:${spec.DimensionsReferenceUploadFileId}`}
							label="Dimensions Reference"
							fileId={spec.DimensionsReferenceUploadFileId ?? null}
							fileName={spec.DimensionsReferenceFileName ?? null}
							onSaveRotated={
								onSaveRotated &&
								((change) =>
									onSaveRotated(
										SECTION_SPEC_FILE_KIND.DIMENSIONS_REFERENCE,
										change,
									))
							}
						/>
					</Grid2>
				)}
			</Grid2>
			<Typography variant="subtitle2" fontWeight={700}>
				Dimensions
			</Typography>
			{isM1 && categoryTabs.length > 0 && (
				<Tabs
					aria-label="Dimension categories"
					value={activeCategoryId}
					variant="fullWidth"
					scrollButtons={false}
					sx={{
						"& .MuiTab-root": {
							minWidth: 0,
							px: 0.5,
							fontSize: "0.75rem",
							overflowWrap: "anywhere",
						},
					}}
					onChange={(_, categoryId: number) =>
						setSelectedCategoryId(categoryId)
					}
				>
					{categoryTabs.map((tab) => (
						<Tab key={tab.id} value={tab.id} label={tab.label} wrapped />
					))}
				</Tabs>
			)}
			<SectionSpecDimensionsGrid
				angleInputs={isM1}
				showType={isM1}
				showBands={
					!isM1 || visibleRows.some((row) => row.CategoryName !== "Samples")
				}
				rows={visibleRows}
				isLoading={false}
				readOnly
				onToggle={() => {}}
				onEdit={() => {}}
			/>
		</>
	);
};
